import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import type { RefObject } from 'react';
import FooterNav from "../components/FooterNav";
import CreatePostModal from "../components/CreatePostModal";
import ExploreDrawer from "../components/ExploreDrawer";
import HomeSidebar from "../components/HomeSidebar";
import "./HomeFeed.css";
import {
    createPost,
    deletePost,
    fetchPostsPage,
    toggleReaction,
    updatePost,
} from "../api/posts";
import { getProfile, searchProfiles, type ProfileSearchUser } from "../api/profile";
import type { Post } from "../types";
import PostItem from "../components/PostItem";
import PostShareModal from "../components/PostShareModal";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useLocation, useNavigate } from "react-router-dom";
import { applyThemeClass } from "../utils/theme";
import {
    Heart,
    Bookmark,
    MessageCircle,
    MoreHorizontal,
    Menu,
    X,
    User,
    Users,
    Clock,
    ListFilter,
    Loader,
    Copy,
    Check,
    PencilLine,
    Save,
    Flag,
    Trash2,
    CornerUpRight,
    Search,
    Eye,
    Image as ImageIcon,
    Smile,
    Repeat2,
    RotateCcw,
} from "lucide-react";
import { createSpaceReport } from "../api/spaces";
import { hasIdentity, requireIdentity } from "../utils/identityGate";

const HOME_COMPOSER_MAX_CHARS = 237;

function getCurrentUsername(): string {
    try {
        const raw = localStorage.getItem("user");
        if (raw) {
            const parsed = JSON.parse(raw);
            return parsed?.username || "";
        }
    } catch {}
    return "";
}

function HamburgerMenu({ isOpen, onToggle, buttonRef }: { isOpen: boolean; onToggle: () => void; buttonRef: RefObject<HTMLButtonElement | null> }) {
    return (
        <button
            ref={buttonRef}
            onClick={onToggle}
            className={`bs-home-mobile-command h-11 w-11 flex items-center justify-center rounded-xl border border-neutral-700/60 bg-neutral-900 text-neutral-200 ${isOpen ? "is-active" : ""}`}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="bs-home-explore-drawer"
        >
            {isOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
    );
}

type QuickFilter = {
    id: string;
    label: string;
    filter?: string;
    sort?: string;
};

const QUICK_FILTERS: QuickFilter[] = [
    { id: "all", label: "All" },
    { id: "mine", label: "My Posts", filter: "mine" },
    { id: "my-spaces", label: "My Spaces", filter: "my-spaces" },
    { id: "24h", label: "24h", filter: "24h" },
    { id: "most-loved", label: "Most Loved", sort: "most-loved" },
    { id: "with-comments", label: "With Comments", filter: "with-comments" },
    { id: "saved", label: "Saved", filter: "saved" },
];

const FEED_SNAPSHOT_KEY = "blackspace.home.feedSnapshot";
const HOME_SURFACE_SNAPSHOT_KEY = "blackspace.home.surfaceSnapshot.v1";
const FEED_COMMENT_RETURN_ANCHOR_KEY = "blackspace.home.commentReturnAnchor";

type FeedSnapshot = {
    posts: Post[];
    cursor: string | null | undefined;
    totalPosts: number | null;
    hasMore: boolean;
    activeFilter: string;
    searchQuery: string;
    scrollY: number;
    postId?: string;
    savedAt: number;
};

type HomeSurfaceSnapshot = {
    profileData: any;
    savedAt: number;
};

let feedMemorySnapshot: FeedSnapshot | null = null;
let homeSurfaceMemorySnapshot: HomeSurfaceSnapshot | null = null;

function hasPostSpaceAccents(posts: Post[]) {
    return posts.every((post) => /^#[0-9a-fA-F]{6}$/.test(post?.space?.accentColor || ""));
}

function readFeedSnapshot(): FeedSnapshot | null {
    try {
        if (feedMemorySnapshot && !hasPostSpaceAccents(feedMemorySnapshot.posts)) {
            feedMemorySnapshot = null;
        }
        if (feedMemorySnapshot && Date.now() - Number(feedMemorySnapshot.savedAt || 0) <= 30 * 60 * 1000) {
            return feedMemorySnapshot;
        }
        const raw = sessionStorage.getItem(FEED_SNAPSHOT_KEY) || localStorage.getItem(FEED_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as FeedSnapshot;
        if (!Array.isArray(parsed.posts) || !hasPostSpaceAccents(parsed.posts)) return null;
        if (Date.now() - Number(parsed.savedAt || 0) > 30 * 60 * 1000) {
            sessionStorage.removeItem(FEED_SNAPSHOT_KEY);
            localStorage.removeItem(FEED_SNAPSHOT_KEY);
            feedMemorySnapshot = null;
            return null;
        }
        feedMemorySnapshot = parsed;
        return parsed;
    } catch {
        return feedMemorySnapshot;
    }
}

function writeFeedSnapshot(snapshot: FeedSnapshot) {
    feedMemorySnapshot = snapshot;
    try {
        sessionStorage.setItem(FEED_SNAPSHOT_KEY, JSON.stringify(snapshot));
        localStorage.setItem(FEED_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
        try {
            sessionStorage.setItem(FEED_SNAPSHOT_KEY, JSON.stringify(snapshot));
        } catch {}
    }
}

function readHomeSurfaceSnapshot(): HomeSurfaceSnapshot | null {
    try {
        if (homeSurfaceMemorySnapshot && Date.now() - Number(homeSurfaceMemorySnapshot.savedAt || 0) <= 30 * 60 * 1000) {
            return homeSurfaceMemorySnapshot;
        }
        const raw = sessionStorage.getItem(HOME_SURFACE_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as HomeSurfaceSnapshot;
        if (Date.now() - Number(parsed.savedAt || 0) > 30 * 60 * 1000) {
            sessionStorage.removeItem(HOME_SURFACE_SNAPSHOT_KEY);
            homeSurfaceMemorySnapshot = null;
            return null;
        }
        homeSurfaceMemorySnapshot = {
            profileData: parsed.profileData || null,
            savedAt: Number(parsed.savedAt || Date.now()),
        };
        return homeSurfaceMemorySnapshot;
    } catch {
        return homeSurfaceMemorySnapshot;
    }
}

function writeHomeSurfaceSnapshot(snapshot: HomeSurfaceSnapshot) {
    if (!hasIdentity()) return;
    homeSurfaceMemorySnapshot = snapshot;
    try {
        sessionStorage.setItem(HOME_SURFACE_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
        // Feed surface memory is polish; the page can always refetch.
    }
}

function readCommentReturnAnchor() {
    try {
        return sessionStorage.getItem(FEED_COMMENT_RETURN_ANCHOR_KEY) || "";
    } catch {
        return "";
    }
}

function writeCommentReturnAnchor(postId: string) {
    try {
        if (postId) sessionStorage.setItem(FEED_COMMENT_RETURN_ANCHOR_KEY, postId);
        else sessionStorage.removeItem(FEED_COMMENT_RETURN_ANCHOR_KEY);
    } catch {
        // In-memory refs still handle the common in-app navigation path.
    }
}

function iconForFilter(id: string) {
    if (id === "mine") return <User size={14} />;
    if (id === "my-spaces") return <Users size={14} />;
    if (id === "24h") return <Clock size={14} />;
    if (id === "most-loved") return <Heart size={14} />;
    if (id === "with-comments") return <MessageCircle size={14} />;
    if (id === "saved") return <Bookmark size={14} />;
    return <ListFilter size={14} />;
}

function normalizeProfileSearch(query: string) {
    return query.trim().replace(/^@+/, "");
}

function textMatchesQuery(value: unknown, normalizedQuery: string) {
    return String(value || "").toLowerCase().includes(normalizedQuery);
}

function profileMatchesQuery(user: ProfileSearchUser, query: string) {
    const normalized = normalizeProfileSearch(query).toLowerCase();
    if (normalized.length < 2) return false;
    return textMatchesQuery(user.username, normalized) || textMatchesQuery(user.displayName, normalized);
}

function filterProfileResults(users: ProfileSearchUser[], query: string) {
    const normalized = normalizeProfileSearch(query).toLowerCase();
    return users
        .filter((user) => profileMatchesQuery(user, normalized))
        .sort((a, b) => {
            const aUsername = String(a.username || "").toLowerCase();
            const bUsername = String(b.username || "").toLowerCase();
            const aExact = aUsername === normalized ? 0 : aUsername.startsWith(normalized) ? 1 : 2;
            const bExact = bUsername === normalized ? 0 : bUsername.startsWith(normalized) ? 1 : 2;
            return aExact - bExact || aUsername.localeCompare(bUsername);
        });
}

function postMatchesQuery(post: Post, query: string) {
    const normalized = query.trim().toLowerCase();
    if (normalized.length < 2) return true;
    return (
        textMatchesQuery(post.content, normalized) ||
        textMatchesQuery(post.author?.username, normalized) ||
        textMatchesQuery(post.author?.displayName, normalized) ||
        textMatchesQuery(post.space?.name, normalized)
    );
}

function filterPostsForSearch(posts: Post[], query: string) {
    return query.trim().length >= 2 ? posts.filter((post) => postMatchesQuery(post, query)) : posts;
}

function PeopleSearchResults({
    query,
    users,
    loading,
    onOpenProfile,
}: {
    query: string;
    users: ProfileSearchUser[];
    loading: boolean;
    onOpenProfile: (username: string) => void;
}) {
    const normalized = normalizeProfileSearch(query);
    if (normalized.length < 2) return null;

    return (
        <div className="mb-4 rounded-lg border border-neutral-800 bg-neutral-950 p-4 shadow-lg">
            <div className="mb-3 flex items-center gap-2">
                <Users size={16} className="text-cyan-300" />
                <h3 className="text-sm font-semibold text-neutral-100">People</h3>
                <span className="text-xs text-neutral-500">matching "{query.trim()}"</span>
            </div>

            {loading ? (
                <div className="grid gap-3 sm:grid-cols-2">
                    {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="h-16 animate-pulse rounded-lg border border-neutral-800 bg-neutral-900" />
                    ))}
                </div>
            ) : users.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                    {users.map((user) => {
                        const name = user.displayName || user.username;
                        return (
                            <button
                                key={user.id}
                                type="button"
                                onClick={() => onOpenProfile(user.username)}
                                className="group flex min-w-0 items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-900 p-3 text-left transition-colors hover:border-cyan-700 hover:bg-neutral-900/80"
                            >
                                {user.avatarUrl ? (
                                    <img
                                        src={user.avatarUrl}
                                        alt=""
                                        className="h-11 w-11 shrink-0 rounded-lg border border-neutral-700 object-cover"
                                    />
                                ) : (
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800 text-sm font-bold text-cyan-300">
                                        {name.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-white group-hover:text-cyan-200">
                                        {name}
                                    </p>
                                    <p className="truncate text-xs text-neutral-500">@{user.username}</p>
                                </div>
                                <span className="shrink-0 rounded-md border border-neutral-700 px-2 py-1 text-xs font-semibold text-neutral-300 group-hover:border-cyan-700 group-hover:text-cyan-200">
                                    Profile
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <p className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm text-neutral-500">
                    No people found for "{query.trim()}".
                </p>
            )}
        </div>
    );
}

function mergeUniquePosts(existing: Post[], incoming: Post[]) {
    const seen = new Set<string>();
    const merged: Post[] = [];

    [...existing, ...incoming].forEach((post) => {
        if (!post?.id || seen.has(post.id)) return;
        seen.add(post.id);
        merged.push(post);
    });

    return merged;
}

function refreshPostSpaceAccents(posts: Post[], freshPosts: Post[]) {
    const accents = new Map<string, string>();
    for (const post of freshPosts) {
        const accent = post.space?.accentColor;
        if (post.space?.id && accent && /^#[0-9a-fA-F]{6}$/.test(accent)) {
            accents.set(post.space.id, accent);
        }
    }
    let changed = false;
    const refreshed = posts.map((post) => {
        const accent = accents.get(post.space?.id);
        if (!accent || accent === post.space?.accentColor) return post;
        changed = true;
        return { ...post, space: { ...post.space, accentColor: accent } };
    });
    return changed ? refreshed : posts;
}

// --- Post Actions Component (updated) ---
type ReactionCounts = { likes: number; loves: number; dislikes: number; saves: number; comments: number };

type PostActionsProps = {
    post?: Post;
    availableActions?: string[];
    initialCounts?: ReactionCounts;
    onReactionUpdated?: (postId: string, reactions: any, userReaction: string | null, userSaved?: boolean) => void;
    onPostDeleted?: (postId: string) => void;
    onPostUpdated?: (post: Post) => void;
    onOpenComments?: (postId: string) => void;
    readOnly?: boolean;
};

function PostActions({ post, availableActions, initialCounts = { likes: 0, loves: 0, dislikes: 0, saves: 0, comments: 0 }, onReactionUpdated, onPostDeleted, onPostUpdated, onOpenComments, readOnly = false }: PostActionsProps) {
    const menuRef = useRef<HTMLDivElement | null>(null);
    const canEditPost = Boolean(post?.canEdit);
    const canDeletePost = Boolean(post?.canDelete);
    const canReportPost = Boolean(post?.canReport);
    const deriveCounts = (): ReactionCounts => {
        if (post?.reactions) {
            const r: any = post.reactions;
            return {
                likes: r.like ?? r.counts?.like ?? initialCounts.likes,
                loves: r.love ?? r.counts?.love ?? initialCounts.loves,
                dislikes: r.dislike ?? r.counts?.dislike ?? initialCounts.dislikes,
                saves: r.save ?? r.counts?.save ?? initialCounts.saves,
                comments: r.comment ?? r.counts?.comment ?? initialCounts.comments,
            };
        }
        return initialCounts;
    };

    const [counts, setCounts] = useState<ReactionCounts>(deriveCounts());
    const [userReaction, setUserReaction] = useState<string | null>(post?.userReaction ?? null);
    const [userSaved, setUserSaved] = useState(Boolean(post?.userSaved));
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(post?.content || "");
    const [editError, setEditError] = useState<string | null>(null);
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        setCounts(deriveCounts());
        setUserReaction(post?.userReaction ?? null);
        setUserSaved(Boolean(post?.userSaved));
        setEditText(post?.content || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post?.id, post?.reactions, post?.userReaction, post?.userSaved]);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [menuOpen]);

    const handleAction = async (action: string) => {
        if (!post?.id) return;
        if (action === "comment") {
            onOpenComments?.(String(post.id));
            return;
        }
        if (readOnly) {
            requireIdentity(action === "save" ? "save posts" : "react to posts");
            return;
        }
        if (pendingAction) return;
        setPendingAction(action);
        try {
            const data: any = await toggleReaction(post.id as string, action);
            const r = data.reactions || {};
            const newCounts = {
                likes: r.like ?? r.counts?.like ?? counts.likes,
                loves: r.love ?? r.counts?.love ?? counts.loves,
                dislikes: r.dislike ?? r.counts?.dislike ?? counts.dislikes,
                saves: r.save ?? r.counts?.save ?? counts.saves,
                comments: r.comment ?? r.counts?.comment ?? counts.comments,
            } as ReactionCounts;
            setCounts(newCounts);
            setUserReaction(data.userReaction ?? null);
            if (action === "save") setUserSaved(Boolean(data.userSaved));
            if (onReactionUpdated) onReactionUpdated(post.id as string, r, data.userReaction ?? null, data.userSaved);
        } catch (err: any) {
            console.error('Reaction API failed:', err);
        } finally {
            setPendingAction(null);
        }
    };

    const copyValue = async (label: string, value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            window.setTimeout(() => setCopied(null), 1200);
        } catch (err) {
            console.error("Copy failed:", err);
        }
    };

    const startEdit = () => {
        if (!post?.id || !canEditPost) return;
        setEditText(post.content || "");
        setEditError(null);
        setEditing(true);
        setMenuOpen(false);
    };

    const openShare = () => {
        if (!post?.id) return;
        setMenuOpen(false);
        setShareOpen(true);
    };

    const saveEdit = async () => {
        if (!post?.id || !canEditPost) return;
        const next = editText.trim();
        if (!next) {
            setEditError("Post cannot be empty.");
            return;
        }
        if (next === (post.content || "").trim()) {
            setEditing(false);
            return;
        }
        setCopied("edit-pending");
        setEditError(null);
        try {
            const updated = await updatePost(String(post.id), next);
            onPostUpdated?.(updated);
            setEditing(false);
            setCopied("edit");
            window.setTimeout(() => setCopied(null), 900);
        } catch (err: any) {
            setEditError(err?.message || "Could not edit post.");
            setCopied(null);
        }
    };

    const reportPost = async () => {
        if (!post?.id) return;
        try {
            await createSpaceReport(post.space?.id || "blackspace", {
                targetType: "post",
                targetId: String(post.id),
                reason: "Reported from feed",
                severity: "medium",
            });
            setCopied("report");
            window.setTimeout(() => {
                setCopied(null);
                setMenuOpen(false);
            }, 900);
        } catch (err) {
            console.error("Report failed:", err);
        }
    };

    const deleteCurrentPost = async () => {
        if (!post?.id) return;
        const confirmed = window.confirm("Delete this post from the feed?");
        if (!confirmed) return;
        setCopied("delete-pending");
        try {
            await deletePost(String(post.id), "Deleted from feed menu");
            setCopied("delete");
            onPostDeleted?.(String(post.id));
            window.setTimeout(() => {
                setCopied(null);
                setMenuOpen(false);
            }, 700);
        } catch (err: any) {
            console.error("Delete failed:", err);
            setCopied("delete-error");
            window.setTimeout(() => setCopied(null), 1400);
        }
    };

    const actionConfig: any = {
        love: { icon: Heart, color: "text-fuchsia-300", hover: "hover:text-fuchsia-300", count: counts.loves },
        dislike: { icon: Repeat2, color: "text-emerald-300", hover: "hover:text-emerald-300", count: counts.dislikes },
        comment: { icon: MessageCircle, color: "text-violet-200", hover: "hover:text-violet-200", count: counts.comments },
        save: { icon: Bookmark, color: "text-cyan-200", hover: "hover:text-cyan-200", count: counts.saves },
    };
    const visibleFeedActions = ["love", "dislike", "comment", "save"].filter((action) => {
        const allowed = availableActions || post?.allowedActions;
        return !allowed || allowed.includes(action);
    });

    return (
        <>
        <div ref={menuRef} className="bs-home-card-menu absolute inset-auto right-3 top-3 z-20 shrink-0 sm:right-4 sm:top-4">
            <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="bs-home-post-menu-button inline-flex h-7 w-7 items-center justify-center text-neutral-400 transition hover:-translate-y-0.5 hover:text-white sm:h-8 sm:w-8"
                aria-expanded={menuOpen}
                aria-label="Post options"
            >
                <MoreHorizontal size={18} />
            </button>
            {menuOpen && (
                <div className="bs-menu bs-home-post-menu absolute right-0 top-8 z-30 w-36 overflow-hidden rounded-xl border shadow-2xl shadow-black/45 sm:top-9">
                    {canEditPost && (
                        <button
                            type="button"
                            onClick={startEdit}
                            className="bs-menu-item flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-semibold"
                        >
                            <PencilLine size={13} className="text-cyan-300" />
                            Edit
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => copyValue("text", post?.content || "")}
                        disabled={!post?.content}
                        className="bs-menu-item flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {copied === "text" ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-cyan-400" />}
                        {copied === "text" ? "Copied" : "Copy"}
                    </button>
                    {canDeletePost && (
                        <button
                            type="button"
                            onClick={deleteCurrentPost}
                            disabled={copied === "delete-pending"}
                            className="bs-menu-item flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {copied === "delete" ? (
                                <Check size={13} className="text-emerald-400" />
                            ) : (
                                <Trash2 size={13} className={copied === "delete-error" ? "text-red-300" : "text-red-400"} />
                            )}
                            {copied === "delete-pending" ? "Deleting..." : copied === "delete-error" ? "Cannot delete" : copied === "delete" ? "Deleted" : "Delete"}
                        </button>
                    )}
                    {canReportPost && (
                        <button
                            type="button"
                            onClick={reportPost}
                            className="bs-menu-item flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-semibold"
                        >
                            {copied === "report" ? <Check size={13} className="text-emerald-400" /> : <Flag size={13} className="text-amber-300" />}
                            {copied === "report" ? "Reported" : "Report"}
                        </button>
                    )}
                </div>
            )}
        </div>

        {editing && (
            <div className="mt-4 rounded-2xl border border-cyan-500/25 bg-cyan-500/5 p-3 shadow-[0_0_32px_rgba(34,211,238,0.10)]">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-cyan-200">
                    <PencilLine size={14} />
                    Edit post
                </div>
                <textarea
                    value={editText}
                    onChange={(event) => setEditText(event.target.value.slice(0, 1000))}
                    rows={4}
                    className="w-full resize-none rounded-2xl border border-cyan-900/60 bg-black/70 px-3 py-2 text-sm leading-6 text-neutral-100 outline-none focus:border-cyan-400/70"
                />
                {editError && <p className="mt-2 text-xs font-semibold text-red-300">{editError}</p>}
                <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="text-xs text-neutral-500">{editText.length}/1000</span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setEditing(false);
                                setEditText(post?.content || "");
                                setEditError(null);
                            }}
                            className="rounded-full border border-neutral-800 bg-black/45 px-3 py-2 text-xs font-bold text-neutral-400 hover:border-neutral-700 hover:text-white"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={saveEdit}
                            disabled={copied === "edit-pending" || !editText.trim()}
                            className="inline-flex items-center gap-2 rounded-full border border-cyan-500/50 bg-cyan-500/20 px-3 py-2 text-xs font-black text-cyan-50 hover:bg-cyan-500/30 disabled:opacity-50"
                        >
                            <Save size={14} />
                            {copied === "edit-pending" ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        <div className="bs-home-post-actions mt-3 flex items-center border-t border-white/10 pt-2.5 text-sm text-neutral-400 sm:mt-4 sm:pt-3">
            <div className="grid w-[13.25rem] grid-cols-4 items-center gap-2 sm:w-[14.5rem]">
                {visibleFeedActions.map((action) => {
                    const config = actionConfig[action as keyof typeof actionConfig];
                    if (!config) return null;
                    const Icon = config.icon;
                    const isActive = action === "save" ? userSaved : userReaction === action;

                    const reactionGroupActive = userReaction === "love" || userReaction === "dislike";
                    const isReaction = action === "love" || action === "dislike";

                    if (isReaction && reactionGroupActive && !isActive) {
                        return <span key={action} className="h-8" aria-hidden="true" />;
                    }

                    return (
                        <button
                            key={action}
                            onClick={() => handleAction(action)}
                            disabled={pendingAction === action}
                            className={`group inline-flex h-8 w-full items-center justify-center gap-1 rounded-full text-sm transition duration-200 hover:-translate-y-0.5 hover:bg-white/[0.035] disabled:cursor-wait disabled:opacity-70 sm:h-9 ${isActive ? config.color + " drop-shadow-[0_0_10px_currentColor]" : config.hover}`}
                            aria-pressed={isActive}
                            title={action === "save" && userSaved ? "saved" : action}
                        >
                            <Icon size={15} className={`${isActive ? "fill-current" : ""}`} strokeWidth={2.15} />
                            <span className={`min-w-[0.7rem] text-left text-[11px] font-semibold sm:text-xs ${isActive ? config.color : "text-neutral-300"}`}>
                                {config.count || 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            <button
                type="button"
                onClick={openShare}
                className="group ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition duration-200 hover:-translate-y-0.5 hover:bg-white/[0.035] hover:text-violet-200 sm:h-9 sm:w-9"
                title="Share"
                aria-label="Share post"
            >
                <CornerUpRight size={15} strokeWidth={2.15} />
            </button>
        </div>
        {post && (
            <PostShareModal
                post={post}
                open={shareOpen}
                onClose={() => setShareOpen(false)}
            />
        )}
        </>
    );
}

// Skeleton Loader
function PostSkeleton() {
    return (
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 animate-pulse">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-neutral-800 rounded-full"></div>
                <div className="flex-1">
                    <div className="h-4 bg-neutral-800 rounded w-24 mb-2"></div>
                    <div className="h-3 bg-neutral-800 rounded w-16"></div>
                </div>
            </div>
            <div className="h-4 bg-neutral-800 rounded w-full mb-3"></div>
            <div className="h-4 bg-neutral-800 rounded w-3/4 mb-4"></div>
            <div className="flex gap-4">
                <div className="h-8 w-8 bg-neutral-800 rounded-full"></div>
                <div className="h-8 w-8 bg-neutral-800 rounded-full"></div>
                <div className="h-8 w-8 bg-neutral-800 rounded-full"></div>
            </div>
        </div>
    );
}

type HomeProps = {
    keepAliveActive?: boolean;
};

export default function Home({ keepAliveActive = true }: HomeProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = (location.state as { restoreFeed?: boolean; postId?: string; pulseDraft?: string } | null) || {};
    const routeRestorePostId = routeState.postId ? String(routeState.postId) : "";
    const restoredFeedRef = useRef<FeedSnapshot | null>(routeState.restoreFeed === false ? null : readFeedSnapshot());
    const filterChangeReadyRef = useRef(false);
    const restoredScrollDoneRef = useRef(false);
    const newPostCheckDoneRef = useRef(false);
    const feedRecoveryAttemptedRef = useRef(false);
    const wasKeepAliveActiveRef = useRef(keepAliveActive);
    const commentReturnAnchorRef = useRef(readCommentReturnAnchor());
    const restoredHomeSurfaceRef = useRef<HomeSurfaceSnapshot | null>(readHomeSurfaceSnapshot());
    const feedSnapshotPostIdRef = useRef(routeRestorePostId || restoredFeedRef.current?.postId || "");
    const latestFeedStateRef = useRef<FeedSnapshot | null>(restoredFeedRef.current);
    const [showModal, setShowModal] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuButtonRef = useRef<HTMLButtonElement | null>(null);
    const footerRef = useRef<HTMLElement | null>(null);
    const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem("theme") || "Light");
    const currentUsername = getCurrentUsername();
    const isAnonymous = !hasIdentity();
    const feedIdentityPresentRef = useRef(!isAnonymous);
    const resetAnonymousSession = useCallback(() => {
        localStorage.removeItem("blackspace_guest_id");
        try {
            window.dispatchEvent(new StorageEvent("storage", { key: "blackspace_guest_id", newValue: null }));
        } catch {
            window.dispatchEvent(new Event("storage"));
        }
        navigate("/login", { replace: true, state: { anonymousResetAt: Date.now() } });
    }, [navigate]);
    const restoredFeed = restoredFeedRef.current;
    const restoredHomeSurface = restoredHomeSurfaceRef.current;

    const [profileData, setProfileData] = useState<any>(() => restoredHomeSurface?.profileData || null);

    const [posts, setPosts] = useState<Post[]>(() => restoredFeed?.posts || []);
    const [cursor, setCursor] = useState<string | null | undefined>(() => restoredFeed?.cursor);
    const [totalPosts, setTotalPosts] = useState<number | null>(() => restoredFeed?.totalPosts ?? null);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(() => restoredFeed?.hasMore ?? true);
    const [error, setError] = useState<string | null>(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState(() => restoredFeed?.searchQuery || "");
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [mobileSearchDraft, setMobileSearchDraft] = useState(() => restoredFeed?.searchQuery || "");
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
    const searchQueryRef = useRef(searchQuery);
    const [peopleResults, setPeopleResults] = useState<ProfileSearchUser[]>([]);
    const [peopleLoading, setPeopleLoading] = useState(false);
    const [filteredPosts, setFilteredPosts] = useState<Post[]>(() => restoredFeed?.posts || []);
    const [activeFilter, setActiveFilter] = useState(() => restoredFeed?.activeFilter || "all");
    const [promptMessage, setPromptMessage] = useState("");
    const [sidebarRevision, setSidebarRevision] = useState(0);
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerText, setComposerText] = useState("");
    const [composerAdvancedOpen, setComposerAdvancedOpen] = useState(false);
    const [composerSubmitting, setComposerSubmitting] = useState(false);
    const [composerError, setComposerError] = useState<string | null>(null);
    const [restoredPostId, setRestoredPostId] = useState<string | null>(() => routeRestorePostId || restoredFeed?.postId || null);
    const [newerPostCount, setNewerPostCount] = useState(0);
    const pulseDraftAppliedRef = useRef(false);

    useEffect(() => {
        if (pulseDraftAppliedRef.current) return;
        const draft = String(routeState.pulseDraft || "").trim();
        if (!draft) return;
        pulseDraftAppliedRef.current = true;
        setComposerText(draft.slice(0, HOME_COMPOSER_MAX_CHARS));
        setComposerOpen(true);
    }, [routeState.pulseDraft]);

    const getVisibleFeedPostId = useCallback(() => {
        if (!keepAliveActive) return "";
        const candidates = Array.from(document.querySelectorAll<HTMLElement>('[id^="post-"]'));
        const visible = candidates
            .map((element) => ({ element, rect: element.getBoundingClientRect() }))
            .filter(({ rect }) => rect.bottom > 96 && rect.top < window.innerHeight - 96)
            .sort((a, b) => Math.abs(a.rect.top - 120) - Math.abs(b.rect.top - 120));
        const id = visible[0]?.element.id || "";
        return id.startsWith("post-") ? id.slice(5) : "";
    }, [keepAliveActive]);

    const checkForNewPosts = useCallback(async () => {
        const latest = latestFeedStateRef.current;
        const currentPosts = latest?.posts || [];
        if (!keepAliveActive || currentPosts.length === 0) return;

        try {
            const selectedFilter = QUICK_FILTERS.find((item) => item.id === latest?.activeFilter);
            const page: any = await fetchPostsPage(undefined, {
                filter: selectedFilter?.filter,
                sort: selectedFilter?.sort,
                q: latest?.searchQuery.trim() || undefined,
            });
            const freshItems = Array.isArray(page?.items) ? page.items : [];
            setPosts((current) => refreshPostSpaceAccents(current, freshItems));
            const restoredIds = new Set(currentPosts.map((post) => String(post.id)));
            const countBeforeKnown = freshItems.findIndex((post: Post) => restoredIds.has(String(post.id)));
            const count = countBeforeKnown >= 0
                ? countBeforeKnown
                : Math.max(0, (typeof page?.total === "number" ? page.total : freshItems.length) - currentPosts.length);
            setNewerPostCount(count);
        } catch (err) {
            console.error("New post check failed:", err);
        }
    }, [keepAliveActive]);

    const persistCurrentFeedSnapshot = useCallback((postId?: string) => {
        if (feedIdentityPresentRef.current !== hasIdentity()) return;
        const latest = latestFeedStateRef.current;
        if (!latest || latest.posts.length === 0) return;
        const visiblePostId = postId || getVisibleFeedPostId() || latest.postId || feedSnapshotPostIdRef.current;
        const snapshot: FeedSnapshot = {
            ...latest,
            scrollY: window.scrollY,
            postId: visiblePostId,
            savedAt: Date.now(),
        };
        feedSnapshotPostIdRef.current = snapshot.postId || "";
        latestFeedStateRef.current = snapshot;
        writeFeedSnapshot(snapshot);
    }, [getVisibleFeedPostId]);

    useLayoutEffect(() => {
        const wasActive = wasKeepAliveActiveRef.current;
        if (wasActive && !keepAliveActive) {
            persistCurrentFeedSnapshot(getVisibleFeedPostId());
        }

        wasKeepAliveActiveRef.current = keepAliveActive;

        if (wasActive || !keepAliveActive) return;

        const latest = latestFeedStateRef.current;
        const commentReturnAnchor = commentReturnAnchorRef.current || readCommentReturnAnchor();
        const targetPostId = routeRestorePostId || commentReturnAnchor || feedSnapshotPostIdRef.current || latest?.postId || "";
        if (routeRestorePostId) {
            feedSnapshotPostIdRef.current = routeRestorePostId;
            setRestoredPostId(routeRestorePostId);
        }
        const target = targetPostId ? document.getElementById(`post-${targetPostId}`) : null;
        if (target) {
            target.scrollIntoView({ behavior: "auto", block: "center" });
        } else if (latest?.scrollY) {
            window.scrollTo({ top: latest.scrollY, behavior: "auto" });
        }
        if (commentReturnAnchor && targetPostId === commentReturnAnchor) {
            commentReturnAnchorRef.current = "";
            writeCommentReturnAnchor("");
            feedSnapshotPostIdRef.current = commentReturnAnchor;
        }
        const frame = window.requestAnimationFrame(() => {
            void checkForNewPosts();
        });

        return () => window.cancelAnimationFrame(frame);
    }, [checkForNewPosts, getVisibleFeedPostId, keepAliveActive, persistCurrentFeedSnapshot, routeRestorePostId]);

    useEffect(() => {
        setFilteredPosts(posts);
    }, [posts]);

    useEffect(() => {
        latestFeedStateRef.current = {
            posts,
            cursor,
            totalPosts,
            hasMore,
            activeFilter,
            searchQuery,
            scrollY: window.scrollY,
            postId: feedSnapshotPostIdRef.current || undefined,
            savedAt: Date.now(),
        };
        if (posts.length > 0) persistCurrentFeedSnapshot();
    }, [activeFilter, cursor, hasMore, persistCurrentFeedSnapshot, posts, searchQuery, totalPosts]);

    useEffect(() => {
        writeHomeSurfaceSnapshot({
            profileData,
            savedAt: Date.now(),
        });
    }, [profileData]);

    useEffect(() => {
        let frame = 0;
        const schedulePersist = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                persistCurrentFeedSnapshot();
            });
        };
        const persistNow = () => persistCurrentFeedSnapshot(getVisibleFeedPostId());

        window.addEventListener("scroll", schedulePersist, { passive: true });
        window.addEventListener("pointerdown", persistNow, { capture: true, passive: true });
        window.addEventListener("pagehide", persistNow);
        document.addEventListener("visibilitychange", persistNow);
        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            window.removeEventListener("scroll", schedulePersist);
            window.removeEventListener("pointerdown", persistNow, { capture: true });
            window.removeEventListener("pagehide", persistNow);
            document.removeEventListener("visibilitychange", persistNow);
        };
    }, [getVisibleFeedPostId, persistCurrentFeedSnapshot]);

    useEffect(() => {
        searchQueryRef.current = searchQuery;
    }, [searchQuery]);

    useEffect(() => {
        if (!mobileSearchOpen) return;
        setMobileSearchDraft(searchQueryRef.current);
        const frame = window.requestAnimationFrame(() => {
            mobileSearchInputRef.current?.focus({ preventScroll: true });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [mobileSearchOpen]);

    useEffect(() => {
        if (!mobileSearchOpen) return;
        const timer = window.setTimeout(() => {
            setSearchQuery((current) => (current === mobileSearchDraft ? current : mobileSearchDraft));
        }, 150);
        return () => window.clearTimeout(timer);
    }, [mobileSearchDraft, mobileSearchOpen]);

    useEffect(() => {
        const peopleQuery = normalizeProfileSearch(searchQuery);
        if (peopleQuery.length < 2) {
            setPeopleResults([]);
            setPeopleLoading(false);
            return;
        }

        let alive = true;
        setPeopleLoading(true);
        const timer = window.setTimeout(() => {
            searchProfiles(peopleQuery)
                .then((users) => {
                    if (alive) setPeopleResults(filterProfileResults(users, peopleQuery));
                })
                .catch((err) => {
                    console.error("People search failed:", err);
                    if (alive) setPeopleResults([]);
                })
                .finally(() => {
                    if (alive) setPeopleLoading(false);
                });
        }, 250);

        return () => {
            alive = false;
            window.clearTimeout(timer);
        };
    }, [searchQuery]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;

        setLoading(true);
        setError(null);

        try {
            // Keep the API result shape flexible while the feed contract evolves.
            const selectedFilter = QUICK_FILTERS.find((item) => item.id === activeFilter);
            const page: any = await fetchPostsPage(cursor, {
                filter: selectedFilter?.filter,
                sort: selectedFilter?.sort,
                q: searchQuery.trim() || undefined,
            });

            const newItems =
                Array.isArray(page?.items)
                    ? page.items
                    : Array.isArray(page)
                        ? page
                        : [];
            const visibleItems = filterPostsForSearch(newItems, searchQuery);

            if (newItems.length === 0) {
                setHasMore(false);
                return;
            }

            setPosts((prev) => mergeUniquePosts(prev, visibleItems));

            const next = page?.nextCursor ?? null;

            setCursor(next);
            if (typeof page?.total === "number") setTotalPosts(page.total);
            setHasMore(Boolean(next));
        } catch (e: any) {
            setError(e?.message || "Failed to load posts");
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [cursor, hasMore, loading, activeFilter, searchQuery]);

    useEffect(() => {
        if (!filterChangeReadyRef.current) {
            filterChangeReadyRef.current = true;
            return;
        }
        setPosts([]);
        setFilteredPosts([]);
        setCursor(undefined);
        setTotalPosts(null);
        setHasMore(true);
        setError(null);
        setNewerPostCount(0);
    }, [activeFilter, searchQuery]);

    useEffect(() => {
        if (!routeRestorePostId || restoredFeedRef.current || feedRecoveryAttemptedRef.current) return;
        if (posts.some((post) => String(post.id) === routeRestorePostId)) return;

        feedRecoveryAttemptedRef.current = true;
        let cancelled = false;

        async function recoverClickedPost() {
            setLoading(true);
            setError(null);
            try {
                const selectedFilter = QUICK_FILTERS.find((item) => item.id === activeFilter);
                let pageCursor: string | null | undefined = undefined;
                let collected: Post[] = [];
                let lastPage: any = null;

                for (let pageIndex = 0; pageIndex < 10; pageIndex += 1) {
                    const page: any = await fetchPostsPage(pageCursor, {
                        filter: selectedFilter?.filter,
                        sort: selectedFilter?.sort,
                        q: searchQuery.trim() || undefined,
                    });
                    lastPage = page;
                    const rawItems = Array.isArray(page?.items) ? page.items : Array.isArray(page) ? page : [];
                    const items = filterPostsForSearch(rawItems, searchQuery);
                    collected = mergeUniquePosts(collected, items);
                    if (collected.some((post) => String(post.id) === routeRestorePostId)) break;
                    pageCursor = page?.nextCursor ?? null;
                    if (!pageCursor) break;
                }

                if (cancelled) return;
                setPosts(collected);
                setFilteredPosts(collected);
                setCursor(lastPage?.nextCursor ?? null);
                setTotalPosts(typeof lastPage?.total === "number" ? lastPage.total : collected.length);
                setHasMore(Boolean(lastPage?.nextCursor));
                setRestoredPostId(routeRestorePostId);
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || "Failed to restore feed position");
                    setHasMore(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        recoverClickedPost();
        return () => {
            cancelled = true;
        };
    }, [activeFilter, routeRestorePostId, searchQuery, posts]);

    useEffect(() => {
        if (routeRestorePostId && !restoredFeedRef.current && !feedRecoveryAttemptedRef.current) return;
        if (posts.length === 0 && hasMore && !loading) {
            loadMore();
        }
    }, [posts.length, hasMore, loading, loadMore, routeRestorePostId]);

    // Fetch logged-in user profile.
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;
        getProfile()
            .then((res) => {
                if (res.success) {
                    setProfileData({ ...res.user, realm: (res as any).realm, isPresence: Boolean((res as any).realm?.isPresence) });
                    // Sync theme from backend ONLY on first login (no localStorage value yet)
                    if (!localStorage.getItem("theme")) {
                        const theme = (res.user as any).settings?.theme || "Light";
                        setCurrentTheme(theme);
                        localStorage.setItem("theme", theme);
                        applyThemeClass(theme);
                    }
                }
            })
            .catch((err) => console.error("Failed to load profile:", err));
    }, []);

    // Escape key closes open feed surfaces.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (mobileSearchOpen || mobileFilterOpen) {
                setMobileSearchOpen(false);
                setMobileFilterOpen(false);
                return;
            }
            if (composerAdvancedOpen || composerOpen) {
                setComposerAdvancedOpen(false);
                setComposerOpen(false);
                setComposerError(null);
                return;
            }
            setIsMenuOpen(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [composerAdvancedOpen, composerOpen, mobileFilterOpen, mobileSearchOpen]);

    // Apply theme.
    useEffect(() => {
        applyThemeClass(currentTheme);
    }, [currentTheme]);

    const sentinelRef = useInfiniteScroll(loadMore, keepAliveActive && hasMore && !loading && !error);

    const feedCountLabel = totalPosts === null
        ? `${filteredPosts.length} in view`
        : `${totalPosts} total / ${filteredPosts.length} in view`;

    const refreshFirstPage = useCallback(async () => {
        const selectedFilter = QUICK_FILTERS.find((item) => item.id === activeFilter);
        const page: any = await fetchPostsPage(undefined, {
            filter: selectedFilter?.filter,
            sort: selectedFilter?.sort,
            q: searchQuery.trim() || undefined,
        });

        const freshItems = filterPostsForSearch(Array.isArray(page?.items) ? page.items : [], searchQuery);
        const uniqueFreshItems = mergeUniquePosts([], freshItems);

        setPosts(uniqueFreshItems);
        setFilteredPosts(uniqueFreshItems);

        const next = page?.nextCursor ?? null;
        setCursor(next);
        setTotalPosts(typeof page?.total === "number" ? page.total : uniqueFreshItems.length);
        setHasMore(Boolean(next));
    }, [activeFilter, searchQuery]);

    const saveFeedSnapshot = useCallback((postId: string) => {
        try {
            feedSnapshotPostIdRef.current = postId;
            persistCurrentFeedSnapshot(postId);
        } catch (err) {
            console.error("Could not preserve feed position:", err);
        }
    }, [persistCurrentFeedSnapshot]);

    const restoredPostIsVisible = Boolean(restoredPostId && filteredPosts.some((post) => String(post.id) === restoredPostId));

    // The highlight must expire even when cached-feed scroll restoration is already done.
    useEffect(() => {
        if (!keepAliveActive || !restoredPostId || !restoredPostIsVisible) return;
        const timer = window.setTimeout(() => {
            setRestoredPostId((current) => current === restoredPostId ? null : current);
        }, 1800);
        return () => window.clearTimeout(timer);
    }, [keepAliveActive, restoredPostId, restoredPostIsVisible]);

    useEffect(() => {
        if (!keepAliveActive || !restoredPostId || restoredScrollDoneRef.current || posts.length === 0) return;
        const frame = window.requestAnimationFrame(() => {
            restoredScrollDoneRef.current = true;
            const target = document.getElementById(`post-${restoredPostId}`);
            if (target) {
                target.scrollIntoView({ behavior: "auto", block: "center" });
            } else if (restoredFeedRef.current?.scrollY) {
                window.scrollTo({ top: restoredFeedRef.current.scrollY, behavior: "auto" });
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [keepAliveActive, posts.length, restoredPostId]);

    useEffect(() => {
        const restored = restoredFeedRef.current;
        if (!keepAliveActive || restoredPostId || restoredScrollDoneRef.current || posts.length === 0 || !restored?.scrollY) return;
        const frame = window.requestAnimationFrame(() => {
            restoredScrollDoneRef.current = true;
            window.scrollTo({ top: restored.scrollY, behavior: "auto" });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [keepAliveActive, posts.length, restoredPostId]);

    useEffect(() => {
        const restored = restoredFeedRef.current;
        if (!restored || newPostCheckDoneRef.current || posts.length === 0) return;
        newPostCheckDoneRef.current = true;
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                const selectedFilter = QUICK_FILTERS.find((item) => item.id === restored.activeFilter);
                const page: any = await fetchPostsPage(undefined, {
                    filter: selectedFilter?.filter,
                    sort: selectedFilter?.sort,
                    q: restored.searchQuery.trim() || undefined,
                });
                if (cancelled) return;
                const freshItems = Array.isArray(page?.items) ? page.items : [];
                setPosts((current) => refreshPostSpaceAccents(current, freshItems));
                const restoredIds = new Set(restored.posts.map((post) => String(post.id)));
                const countBeforeKnown = freshItems.findIndex((post: Post) => restoredIds.has(String(post.id)));
                const count = countBeforeKnown >= 0
                    ? countBeforeKnown
                    : Math.max(0, (typeof page?.total === "number" ? page.total : freshItems.length) - restored.posts.length);
                setNewerPostCount(count);
            } catch (err) {
                console.error("New post check failed:", err);
            }
        }, 700);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [posts.length]);

    const refreshFeedFromNotice = async () => {
        setNewerPostCount(0);
        sessionStorage.removeItem(FEED_SNAPSHOT_KEY);
        localStorage.removeItem(FEED_SNAPSHOT_KEY);
        setPosts([]);
        setFilteredPosts([]);
        setCursor(undefined);
        setTotalPosts(null);
        setHasMore(true);
        setError(null);
        try {
            setLoading(true);
            await refreshFirstPage();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally {
            setLoading(false);
        }
    };

    const handleFastPost = async () => {
        if (isAnonymous) {
            requireIdentity("publish a post");
            return;
        }
        if (!composerText.trim() || composerSubmitting) return;

        setComposerSubmitting(true);
        setComposerError(null);

        try {
            await createPost({
                content: composerText.trim(),
                spaceId: localStorage.getItem("lastSpaceId") || "blackspace",
                spaceName: localStorage.getItem("lastSpace") || "Blackspace",
            });
            setComposerText("");
            setComposerOpen(false);
            setPosts([]);
            setFilteredPosts([]);
            setCursor(undefined);
            setTotalPosts(null);
            setHasMore(true);
            await refreshFirstPage();
            setSidebarRevision((value) => value + 1);
        } catch (err: any) {
            setComposerError(err?.message || "Could not create post");
        } finally {
            setComposerSubmitting(false);
        }
    };

    const openAdvancedComposer = (message = composerText) => {
        if (isAnonymous) {
            requireIdentity("publish a post");
            return;
        }
        setPromptMessage(message.slice(0, HOME_COMPOSER_MAX_CHARS));
        setShowModal(true);
    };

    const openSearchedProfile = (username: string) => {
        persistCurrentFeedSnapshot();
        navigate(`/user/${username}`, { state: { from: "/home", label: "Feed", restoreFeed: true } });
    };

    const openPostComments = (postId: string) => {
        feedSnapshotPostIdRef.current = postId;
        commentReturnAnchorRef.current = postId;
        writeCommentReturnAnchor(postId);
        persistCurrentFeedSnapshot(postId);
        navigate(`/post/${postId}`, { state: { from: "/home", label: "Feed", restoreFeed: true, postId } });
    };


    return (
        <div
            className="bs-page flex min-h-screen flex-col"
            style={keepAliveActive ? undefined : {
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100vh",
                overflow: "hidden",
                visibility: "hidden",
                pointerEvents: "none",
                transform: "translateX(-120vw)",
            }}
            aria-hidden={!keepAliveActive}
        >
            <ExploreDrawer
                id="bs-home-explore-drawer"
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                triggerRef={menuButtonRef}
                footerRef={footerRef}
                isAnonymous={isAnonymous}
                profile={profileData}
                fallbackUsername={currentUsername}
            />

            {/* MAIN CONTENT */}
            <main className="bs-home-premium pt-4 px-3 sm:px-8 lg:px-12 pb-28 w-full max-w-7xl mx-auto flex flex-col gap-4 md:gap-7">

                <header className="bs-home-hero-header grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:gap-4">
                    <HamburgerMenu
                        isOpen={isMenuOpen}
                        onToggle={() => setIsMenuOpen(!isMenuOpen)}
                        buttonRef={menuButtonRef}
                    />

                    <button
                        type="button"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="bs-home-wordmark justify-self-center"
                        aria-label="Back to top"
                    >
                        <span>Black</span><strong>space</strong><i />
                    </button>

                    <div className="relative flex items-center justify-end gap-2 sm:gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setMobileSearchOpen((open) => !open);
                                setMobileFilterOpen(false);
                            }}
                            className={`bs-home-mobile-command h-11 w-11 flex items-center justify-center rounded-xl border border-neutral-700/60 bg-neutral-900 text-neutral-200 ${mobileSearchOpen || searchQuery ? "is-active" : ""}`}
                            aria-label="Search feed"
                            title="Search"
                        >
                            <Search size={20} />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setMobileFilterOpen((open) => !open);
                                setMobileSearchOpen(false);
                            }}
                            className={`bs-home-mobile-command h-11 w-11 flex items-center justify-center rounded-xl border border-neutral-700/60 bg-neutral-900 text-neutral-200 ${mobileFilterOpen || activeFilter !== "all" ? "is-active" : ""}`}
                            aria-label="Filter feed"
                            title="Filter"
                        >
                            <ListFilter size={19} />
                        </button>
                    </div>
                </header>

                {mobileSearchOpen && (
                    <div
                        className="bs-home-mobile-sheet"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <form
                            className="bs-home-mobile-search-form flex items-center gap-2"
                            onSubmit={(event) => {
                                event.preventDefault();
                                const nextQuery = mobileSearchDraft.trim();
                                setSearchQuery(nextQuery);
                            }}
                        >
                            <Search size={17} className="shrink-0 text-cyan-200" />
                            <input
                                ref={mobileSearchInputRef}
                                type="search"
                                name="home-mobile-search"
                                value={mobileSearchDraft}
                                onChange={(event) => setMobileSearchDraft(event.target.value)}
                                autoComplete="off"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="search"
                                inputMode="search"
                                placeholder="Search posts, people, spaces..."
                                className="bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                            />
                            {mobileSearchDraft && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setMobileSearchDraft("");
                                        setSearchQuery("");
                                        mobileSearchInputRef.current?.focus();
                                    }}
                                    className="grid h-8 w-8 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400"
                                    aria-label="Clear search"
                                >
                                    <X size={15} />
                                </button>
                            )}
                        </form>
                    </div>
                )}

                {mobileFilterOpen && (
                    <div className="bs-home-mobile-sheet bs-home-mobile-filter-sheet md:hidden">
                        <div className="bs-home-mobile-filter-grid grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                            {QUICK_FILTERS.filter((filter) => !isAnonymous || !["mine", "my-spaces", "saved"].includes(filter.id)).map((filter) => (
                                <button
                                    key={filter.id}
                                    type="button"
                                    onClick={() => {
                                        setActiveFilter(filter.id);
                                        setMobileFilterOpen(false);
                                    }}
                                    className={`bs-home-mobile-filter-choice ${activeFilter === filter.id ? "is-active" : ""}`}
                                    aria-pressed={activeFilter === filter.id}
                                >
                                    <span className="bs-feed-filter-icon">{iconForFilter(filter.id)}</span>
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_20rem]">
                    <section className="min-w-0">
                        {mobileFilterOpen && (
                            <div className="bs-home-desktop-filters bs-surface mb-3 hidden flex-wrap items-center gap-2 rounded-lg border p-3 shadow-lg md:flex">
                                {QUICK_FILTERS.filter((filter) => !isAnonymous || !["mine", "my-spaces", "saved"].includes(filter.id)).map((filter) => (
                                    <button
                                        key={`desktop-${filter.id}`}
                                        type="button"
                                        onClick={() => {
                                            setActiveFilter(filter.id);
                                            setMobileFilterOpen(false);
                                        }}
                                        className={`bs-feed-filter-chip inline-flex h-9 items-center rounded-md border px-3 text-xs font-medium transition-colors ${activeFilter === filter.id ? "is-active" : ""}`}
                                        aria-pressed={activeFilter === filter.id}
                                    >
                                        <span className="bs-feed-filter-icon mr-1.5">
                                            {iconForFilter(filter.id)}
                                        </span>
                                        {filter.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {isAnonymous ? (
                        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-cyan-300/20 bg-cyan-300/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                            <div className="min-w-0">
                                <p className="text-sm font-black text-white">Browsing anonymously</p>
                                <p className="mt-1 text-xs leading-5 text-neutral-400">Read the public realm without an account. Create an identity when you want to post, react, save, or join.</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
                                <button type="button" onClick={() => requireIdentity("join the conversation")} className="rounded-lg bg-cyan-300 px-3 py-2 text-xs font-black text-black hover:bg-cyan-200">Join in</button>
                                <button
                                    type="button"
                                    onClick={resetAnonymousSession}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-300/20 bg-white/[0.03] text-cyan-100/75 transition hover:border-cyan-300/40 hover:text-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40"
                                    aria-label="Reset anonymous session"
                                    title="Reset anonymous session"
                                >
                                    <RotateCcw size={15} aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        ) : !composerAdvancedOpen ? (
                        <div
                            className={`bs-home-composer mb-5 w-full rounded-xl border p-3 text-left transition sm:p-3.5 ${
                                composerOpen
                                    ? "is-open border-cyan-300/30 bg-cyan-300/[0.025] shadow-[0_0_38px_rgba(8,145,178,0.08)]"
                                    : "border-white/10 bg-transparent hover:border-violet-300/25"
                            }`}
                        >
                            <div className={`flex gap-3 sm:gap-4 ${composerOpen ? "items-start" : "items-center"}`}>
                                {profileData?.avatarUrl ? (
                                    <img
                                        src={profileData.avatarUrl}
                                        alt=""
                                        className="bs-home-composer-avatar h-10 w-10 shrink-0 rounded-lg border border-cyan-300/20 object-cover sm:h-11 sm:w-11"
                                    />
                                ) : (
                                    <div className="bs-home-composer-avatar flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-cyan-300/20 bg-cyan-950/15 text-sm font-black text-cyan-100 sm:h-11 sm:w-11">
                                        {(profileData?.displayName || profileData?.username || currentUsername || "?").slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    {!composerOpen ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setComposerOpen(true);
                                                setComposerError(null);
                                            }}
                                            className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-1 py-1.5 text-left text-sm font-medium leading-snug text-neutral-400 transition hover:text-neutral-200 sm:text-base"
                                        >
                                            <span className="max-w-[12rem] sm:max-w-[15rem]">What's happening?</span>
                                            <span className="flex shrink-0 items-center gap-3 text-violet-300">
                                                <ImageIcon size={20} />
                                                <span className="rounded-md border border-violet-300/70 px-1.5 py-0.5 text-[11px] font-black leading-none">GIF</span>
                                                <Smile size={20} />
                                            </span>
                                        </button>
                                    ) : (
                                        <textarea
                                            value={composerText}
                                            onChange={(event) => setComposerText(event.target.value.slice(0, HOME_COMPOSER_MAX_CHARS))}
                                            rows={3}
                                            maxLength={HOME_COMPOSER_MAX_CHARS}
                                            autoFocus
                                            placeholder="What's happening?"
                                            className="w-full resize-none border-0 bg-transparent px-1 py-1.5 text-sm leading-6 text-neutral-100 outline-none transition placeholder:text-neutral-500 sm:text-base"
                                        />
                                    )}
                                </div>
                            </div>

                            {composerOpen && (
                                <div className="mt-3 flex flex-col gap-3 border-t border-white/[0.08] pt-3">
                                    {composerError && (
                                        <p className="rounded-xl border border-red-900 bg-red-950/30 px-3 py-2 text-xs text-red-300">
                                            {composerError}
                                        </p>
                                    )}
                                    <div className="bs-home-composer-toolbar">
                                        <span className="bs-home-composer-count">
                                            {composerText.length}/{HOME_COMPOSER_MAX_CHARS}
                                        </span>
                                        <div className="bs-home-composer-icon-group">
                                            <button
                                                type="button"
                                                onClick={() => openAdvancedComposer(composerText)}
                                                className="bs-home-composer-icon-action"
                                                title="Image"
                                                aria-label="Open advanced composer for image"
                                            >
                                                <ImageIcon size={17} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openAdvancedComposer(composerText)}
                                                className="bs-home-composer-icon-action"
                                                title="GIF"
                                                aria-label="Open advanced composer for GIF"
                                            >
                                                <span className="text-[10px] font-black">GIF</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openAdvancedComposer(composerText)}
                                                className="bs-home-composer-icon-action"
                                                title="Feeling"
                                                aria-label="Open advanced composer for feeling"
                                            >
                                                <Smile size={17} />
                                            </button>
                                        </div>
                                        <div className="bs-home-composer-action-group">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setComposerAdvancedOpen(true);
                                                    setComposerOpen(false);
                                                    setComposerError(null);
                                                }}
                                                className="bs-home-composer-text-action"
                                            >
                                                Advanced
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setComposerOpen(false);
                                                    setComposerText("");
                                                    setComposerError(null);
                                                }}
                                                className="bs-home-composer-text-action is-muted"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleFastPost}
                                                disabled={!composerText.trim() || composerSubmitting}
                                                className="bs-home-composer-text-action is-primary"
                                            >
                                                {composerSubmitting && <Loader size={12} className="animate-spin" />}
                                                Post
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        ) : (
                            <div className="mb-5">
                                <CreatePostModal
                                    key={`inline-advanced-${composerText}`}
                                    variant="inline"
                                    initialMessage={composerText}
                                    onClose={() => {
                                        setComposerAdvancedOpen(false);
                                        setComposerOpen(true);
                                    }}
                                    onPostCreated={async () => {
                                        setPosts([]);
                                        setFilteredPosts([]);
                                        setCursor(undefined);
                                        setTotalPosts(null);
                                        setHasMore(true);
                                        try {
                                            setLoading(true);
                                            await refreshFirstPage();
                                        } catch (err) {
                                            console.error("Post refresh failed:", err);
                                        } finally {
                                            setLoading(false);
                                            setComposerAdvancedOpen(false);
                                            setComposerOpen(false);
                                            setComposerText("");
                                            setComposerError(null);
                                            setSidebarRevision((value) => value + 1);
                                        }
                                    }}
                                />
                            </div>
                        )}

                        <div className="bs-home-feed-stage">
                            <div className="mb-4 sm:mb-5">
                                <div className="flex min-w-0 items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <h2 className="truncate text-lg font-black leading-tight tracking-[-0.01em] text-white sm:text-2xl">Feeds</h2>
                                    </div>
                                    <span className="bs-home-feed-count inline-flex max-w-[48%] shrink-0 items-center gap-1.5 rounded-full border border-violet-300/25 bg-violet-300/10 px-2.5 py-1 text-[11px] font-semibold text-violet-200 shadow-[0_0_24px_rgba(168,85,247,0.10)] sm:max-w-none sm:px-3 sm:py-1.5 sm:text-xs">
                                        <Eye size={13} className="sm:h-[15px] sm:w-[15px]" />
                                        <span className="truncate">{feedCountLabel}{searchQuery && ` for "${searchQuery}"`}</span>
                                    </span>
                                </div>
                                <p className="mt-1 text-xs font-medium text-neutral-400 sm:text-sm">Stay updated with what matters.</p>
                            </div>

                            {newerPostCount > 0 && (
                                <div className="sticky top-3 z-20 mb-4 flex justify-center">
                                    <button
                                        type="button"
                                        onClick={refreshFeedFromNotice}
                                        className="rounded-md border border-cyan-500/40 bg-cyan-500/15 px-4 py-2 text-xs font-black text-cyan-100 shadow-[0_0_30px_rgba(8,145,178,0.22)] backdrop-blur transition-colors hover:bg-cyan-500/25"
                                    >
                                        {newerPostCount} new post{newerPostCount === 1 ? "" : "s"} available. Refresh feed
                                    </button>
                                </div>
                            )}

                            {!loading && filteredPosts.length === 0 && (
                                <div className="rounded-lg border border-neutral-800 bg-neutral-900/40 px-6 py-12 text-center fade-in">
                                    <div className="mb-4 text-2xl text-cyan-400">No posts</div>
                                    <p className="mb-2 text-lg font-semibold text-neutral-300">
                                        {searchQuery ? "No posts found" : "No posts yet"}
                                    </p>
                                    <p className="mb-6 text-neutral-500">
                                        {searchQuery
                                            ? peopleResults.length > 0
                                                ? "No posts matched. People matches are shown below the feed."
                                                : "Try a different search term or username."
                                            : "Be the first to share something amazing!"}
                                    </p>
                                    {!searchQuery && (
                                        <button
                                            onClick={() => openAdvancedComposer("")}
                                            className="rounded-md bg-cyan-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-600"
                                        >
                                            Create First Post
                                        </button>
                                    )}
                                </div>
                            )}

                            <ul className="space-y-3">
                                {filteredPosts.map((post) => (
                                    <li
                                        key={post.id}
                                        id={`post-${post.id}`}
                                        className={`bs-home-feed-card bs-post-card relative rounded-xl border p-3 transition duration-200 ${
                                            restoredPostId === String(post.id)
                                                ? "ring-2 ring-cyan-400/60 shadow-[0_0_50px_rgba(34,211,238,0.22)]"
                                                : ""
                                        }`}
                                    >
                                        <PostItem
                                            post={post}
                                            currentUsername={currentUsername}
                                            onSelfClick={() => setIsMenuOpen(true)}
                                            onAuthorNavigate={saveFeedSnapshot}
                                        />

                                        <PostActions
                                            post={post}
                                            availableActions={post.allowedActions}
                                            onReactionUpdated={(postId: string, reactions: any, userReaction: string | null, userSaved?: boolean) => {
                                                setPosts(prev => {
                                                    if (activeFilter === "saved" && userSaved === false) {
                                                        return prev.filter(p => p.id !== postId);
                                                    }

                                                    return prev.map(p => {
                                                        if (p.id !== postId) return p;
                                                        return {
                                                            ...p,
                                                            reactions,
                                                            userReaction,
                                                            ...(typeof userSaved === "boolean" ? { userSaved } : {}),
                                                        };
                                                    });
                                                });
                                            }}
                                            onPostDeleted={(postId: string) => {
                                                setPosts((prev) => prev.filter((item) => String(item.id) !== postId));
                                                setFilteredPosts((prev) => prev.filter((item) => String(item.id) !== postId));
                                                setTotalPosts((prev) => (typeof prev === "number" ? Math.max(0, prev - 1) : prev));
                                                setSidebarRevision((value) => value + 1);
                                            }}
                                            onPostUpdated={(updated: Post) => {
                                                setPosts((prev) => prev.map((item) => String(item.id) === String(updated.id) ? { ...item, ...updated } : item));
                                                setFilteredPosts((prev) => prev.map((item) => String(item.id) === String(updated.id) ? { ...item, ...updated } : item));
                                            }}
                                            onOpenComments={openPostComments}
                                            readOnly={isAnonymous}
                                        />
                                    </li>
                                ))}
                            </ul>

                            {loading && (
                                <div className="mt-6 space-y-4">
                                    {[...Array(3)].map((_, i) => (
                                        <PostSkeleton key={i} />
                                    ))}
                                </div>
                            )}

                            {error && (
                                <div className="py-8 text-center">
                                    <div className="mx-auto max-w-md rounded-lg border border-red-900 bg-red-950/30 p-6">
                                        <p className="mb-4 text-red-200">{error}</p>
                                        <button
                                            onClick={() => { setError(null); setHasMore(true); loadMore(); }}
                                            className="rounded-md bg-red-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                                        >
                                            Retry Loading
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div ref={sentinelRef} className="h-8" />

                            {!hasMore && filteredPosts.length > 0 && (
                                <div className="mt-6 border-t border-neutral-800 py-8 text-center">
                                    <div className="mb-2 text-xl text-cyan-400">Done</div>
                                    <p className="mb-1 text-lg font-semibold text-cyan-400">You're all caught up!</p>
                                    <p className="text-sm text-neutral-500">Come back later for more amazing content</p>
                                </div>
                            )}
                        </div>

                        <PeopleSearchResults
                            query={searchQuery}
                            users={peopleResults}
                            loading={peopleLoading}
                            onOpenProfile={openSearchedProfile}
                        />
                    </section>

                    <HomeSidebar active={keepAliveActive} anonymous={isAnonymous} revision={sidebarRevision} />
                </div>
            </main>

            <footer ref={footerRef} className="fixed bottom-0 left-0 w-full bg-gray-900 border-t border-gray-800 z-50">
                <FooterNav />
            </footer>

 {showModal && (
  <CreatePostModal
    onClose={() => setShowModal(false)}
    initialMessage={promptMessage}
    onPostCreated={async () => {
      // Reset everything.
      setPosts([]);
      setFilteredPosts([]);
      setCursor(undefined);
      setTotalPosts(null);
      setHasMore(true);

      // Re-fetch the first page from backend.
      try {
        setLoading(true);
        await refreshFirstPage();
      } catch (err) {
        console.error("Post refresh failed:", err);
      } finally {
        setLoading(false);
        setShowModal(false);
        setPromptMessage("");
        setComposerText("");
        setComposerOpen(false);
        setSidebarRevision((value) => value + 1);
      }
    }}
  />
)}




            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                .stealth-scroll {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .stealth-scroll::-webkit-scrollbar {
                    width: 0;
                    height: 0;
                }
            `}</style>
        </div>
    );
}
